import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Lock, 
  Shield, 
  Smartphone, 
  Mail, 
  Loader2, 
  AlertTriangle, 
  Check, 
  Settings 
} from 'lucide-react';
import mfaService from '../../services/mfaService';
import MFASetup from '../Auth/MFASetup';
import toast from 'react-hot-toast';

const SecurityTab = ({ userProfile, embedded = false }) => {
  // MFA-related state
  const [mfaStatus, setMfaStatus] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [methodToRemove, setMethodToRemove] = useState(null);
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [trustedDevices, setTrustedDevices] = useState([]);

  // Load MFA status
  const loadMFAStatus = async () => {
    try {
      setMfaLoading(true);
      const [status, devices] = await Promise.all([
        mfaService.getMFAStatus(),
        mfaService.getTrustedDevices().catch(() => [])
      ]);
      setMfaStatus(status);
      setTrustedDevices(devices);
    } catch (error) {
      console.error('Error loading MFA status:', error);
    } finally {
      setMfaLoading(false);
    }
  };

  // Handle MFA setup completion
  const handleMFASetupComplete = () => {
    console.log('[SECURITY TAB] MFA setup completed, refreshing status...');
    setShowMfaSetup(false);
    loadMFAStatus();
  };

  // Handle trusted device removal
  const handleRemoveTrustedDevice = async (deviceId) => {
    try {
      await mfaService.removeTrustedDevice(deviceId);
      toast.success('Trusted device removed');
      loadMFAStatus();
    } catch (error) {
      console.error('Error removing trusted device:', error);
      toast.error('Failed to remove trusted device');
    }
  };

  // Handle MFA method removal - show confirmation modal
  const handleRemoveMFAMethod = (methodId) => {
    console.log('[SECURITY TAB] Remove MFA method clicked:', methodId);
    setMethodToRemove(methodId);
    setShowRemoveConfirm(true);
  };

  // Confirm MFA method removal
  const confirmRemoveMFAMethod = async () => {
    if (!methodToRemove || !passwordConfirmation.trim()) {
      toast.error('Please enter your password to confirm');
      return;
    }
    
    console.log('[SECURITY TAB] Confirming removal of method:', methodToRemove);
    setShowRemoveConfirm(false);
    
    try {
      console.log('[SECURITY TAB] Calling mfaService.removeMFAMethod...');
      await mfaService.removeMFAMethod(methodToRemove, passwordConfirmation);
      console.log('[SECURITY TAB] MFA method removed successfully');
      toast.success('MFA method removed successfully');
      
      await loadMFAStatus();
    } catch (error) {
      console.error('[SECURITY TAB] Error removing MFA method:', error);
      
      let errorMessage = 'Failed to remove MFA method';
      
      if (error.response?.status === 400) {
        const errorData = error.response?.data;
        if (errorData?.detail) {
          errorMessage = errorData.detail;
        } else if (errorData?.non_field_errors) {
          errorMessage = errorData.non_field_errors[0];
        } else {
          errorMessage = 'Invalid request. Please try again.';
        }
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to remove this MFA method.';
      } else if (error.response?.status === 404) {
        errorMessage = 'MFA method not found. It may have already been removed.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setMethodToRemove(null);
      setPasswordConfirmation('');
    }
  };

  // Cancel MFA method removal
  const cancelRemoveMFAMethod = () => {
    console.log('[SECURITY TAB] MFA removal cancelled by user');
    setShowRemoveConfirm(false);
    setMethodToRemove(null);
    setPasswordConfirmation('');
  };

  // Load MFA status on mount
  useEffect(() => {
    loadMFAStatus();
  }, []);

  const sectionClass = embedded
    ? 'rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800'
    : 'card overflow-hidden';

  return (
    <div className="space-y-6">
      {/* Header for embedded mode */}
      {embedded && (
        <div className="mb-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Security Settings</h2>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">Manage your password and authentication methods</p>
        </div>
      )}

      {/* Password Section */}
      <div className={sectionClass}>
        {!embedded && (
          <div className="card-band">
            <h2 className="card-band-title flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Password
            </h2>
          </div>
        )}
        <div className={embedded ? "" : "p-6"}>
          {embedded && (
            <h3 className="mb-1.5 text-base font-semibold text-gray-900 dark:text-gray-100">Password</h3>
          )}
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            Keep your account secure by using a strong, unique password.
          </p>
          <Link 
            to="/change-password"
            className="btn btn-primary"
          >
            <Lock className="h-4 w-4" />
            Change Password
          </Link>
        </div>
      </div>

      {/* MFA Section */}
      <div className={sectionClass}>
        {!embedded && (
          <div className="card-band">
            <h2 className="card-band-title flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Multi-Factor Authentication
            </h2>
          </div>
        )}
        <div className={embedded ? "" : "p-6"}>
          {embedded && (
            <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100">Multi-Factor Authentication</h3>
          )}
          {mfaLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
            </div>
          ) : mfaStatus ? (
            <div className="space-y-6">
              {/* MFA Status Overview */}
              <div className="rounded-lg border border-gray-200/70 bg-gray-50 p-4 dark:border-white/[0.07] dark:bg-white/[0.03]">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`mr-3 h-2.5 w-2.5 flex-shrink-0 rounded-full ${
                      mfaStatus.mfa_setup_completed ? 'bg-success-500' : 'bg-warning-500'
                    }`}></div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {mfaStatus.mfa_setup_completed 
                          ? 'MFA Enabled' 
                          : (mfaStatus.mfa_required ? 'MFA Setup Required' : 'MFA Available')
                        }
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {mfaStatus.mfa_setup_completed 
                          ? 'Your account is protected with multi-factor authentication' 
                          : (mfaStatus.mfa_required 
                              ? 'Multi-factor authentication is required for your account'
                              : 'Multi-factor authentication is optional for your account'
                            )
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* MFA Required Notice */}
                {mfaStatus.mfa_required && !mfaStatus.mfa_setup_completed && (
                  <div className="mb-4 rounded-lg border border-warning-500/30 bg-warning-50 p-4 dark:bg-warning-500/10">
                    <div className="flex items-start gap-2.5">
                      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning-600 dark:text-warning-400" />
                      <div>
                        <p className="text-sm font-medium text-warning-800 dark:text-warning-400">
                          MFA Setup Required
                        </p>
                        <p className="mt-1 text-sm text-warning-700 dark:text-warning-400/90">
                          {mfaStatus.required_by_study 
                            ? `MFA is required by your study administrator. ${mfaStatus.setup_deadline ? `Setup deadline: ${new Date(mfaStatus.setup_deadline).toLocaleDateString()}` : ''}` 
                            : 'MFA is required for your account.'
                          }
                        </p>
                        <button
                          onClick={() => setShowMfaSetup(true)}
                          className="btn btn-primary btn-sm mt-3"
                        >
                          Set Up MFA Now
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* MFA Methods */}
                {mfaStatus.mfa_setup_completed && mfaStatus.mfa_methods && mfaStatus.mfa_methods.length > 0 && (
                  <div>
                    <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Active MFA Methods</h4>
                    <div className="space-y-2">
                      {mfaStatus.mfa_methods.map((method, index) => {
                        const isOnlyMethod = mfaStatus.mfa_methods.length === 1;
                        const isRemoveDisabled = isOnlyMethod && mfaStatus.mfa_required;
                        
                        return (
                          <div key={method.id || index} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                            <div className="flex min-w-0 items-center gap-3">
                              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                                {method.type === 'totp' ? (
                                  <Smartphone className="h-[18px] w-[18px]" />
                                ) : (
                                  <Mail className="h-[18px] w-[18px]" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {method.type === 'totp' ? 'Authenticator App' : 'Email Verification'}
                                </p>
                                <p className="break-words text-sm text-gray-600 dark:text-gray-400">
                                  {method.type === 'totp' 
                                    ? `Device: ${method.device_name || 'Unknown Device'}` 
                                    : `Codes sent to ${userProfile?.email}`
                                  }
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {method.is_primary && <span className="font-medium text-primary-700 dark:text-primary-400">Primary • </span>}
                                  Added: {new Date(method.created_at).toLocaleDateString()}
                                  {method.last_used && (
                                    <span> • Last used: {new Date(method.last_used).toLocaleDateString()}</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-shrink-0 items-center gap-1">
                              <Check className="h-4 w-4 text-success-600 dark:text-success-400" />
                              <button
                                onClick={() => handleRemoveMFAMethod(method.id)}
                                disabled={isRemoveDisabled}
                                className="btn btn-sm btn-danger-soft"
                                title={isRemoveDisabled ? 'Cannot remove your only MFA method when MFA is required' : 'Remove this MFA method'}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Add Another Device Button */}
                    <div className="mt-4 flex justify-center">
                      <button
                        onClick={() => setShowMfaSetup(true)}
                        className="btn btn-secondary"
                      >
                        <Shield className="h-4 w-4" />
                        Add Another Device
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Setup MFA Button - Only show if MFA is not required (optional setup) */}
              {!mfaStatus.mfa_setup_completed && !mfaStatus.mfa_required && (
                <div className="flex justify-center">
                  <button
                    onClick={() => setShowMfaSetup(true)}
                    className="btn btn-primary"
                  >
                    <Shield className="h-4 w-4" />
                    Set Up Multi-Factor Authentication
                  </button>
                </div>
              )}

              {/* Trusted Devices */}
              {trustedDevices && trustedDevices.length > 0 && (
                <div>
                  <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    <Settings className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    Trusted Devices
                  </h4>
                  <div className="space-y-2">
                    {trustedDevices.map((device) => (
                      <div key={device.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {device.device_name || 'Unknown Device'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Added: {new Date(device.created_at).toLocaleDateString()}
                            {device.expires_at && (
                              <span> • Expires: {new Date(device.expires_at).toLocaleDateString()}</span>
                            )}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemoveTrustedDevice(device.id)}
                          className="btn btn-sm btn-danger-soft flex-shrink-0"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center py-8 text-center">
              <Shield className="mb-3 h-6 w-6 text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
              <p className="text-sm text-gray-600 dark:text-gray-400">Unable to load MFA status</p>
              <button
                onClick={loadMFAStatus}
                className="btn btn-secondary btn-sm mt-3"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div>

      {/* MFA Setup Modal */}
      {showMfaSetup && (
        <MFASetup
          onSetupComplete={handleMFASetupComplete}
          onCancel={mfaStatus?.mfa_required || userProfile?.role === 'Program Admin' ? null : () => setShowMfaSetup(false)}
          userEmail={userProfile?.email}
          isRequired={mfaStatus?.mfa_required || userProfile?.role === 'Program Admin'}
        />
      )}

      {/* MFA Remove Confirmation Modal */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70">
          <div className="w-full max-w-md overflow-hidden rounded-xl bg-white px-6 pt-5 shadow-overlay ring-1 ring-gray-900/5 animate-slide-up dark:bg-gray-800 dark:ring-white/10">
            <h3 className="mb-3 flex items-center gap-2.5 text-base font-semibold text-gray-900 dark:text-gray-50">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 text-danger-600 dark:text-danger-400" strokeWidth={1.75} />
              Remove MFA Method
            </h3>
            <p className="mb-4 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
              {mfaStatus.mfa_methods.length > 1 
                ? 'Are you sure you want to remove this MFA method? This will reduce your account security.'
                : 'Are you sure you want to remove this MFA method? You will need to set up another MFA method before you can remove this one if MFA is required for your account.'
              }
            </p>
            
            <div className="mb-6">
              <label className="label">
                Enter your password to confirm:
              </label>
              <input
                type="password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                className="input"
                placeholder="Enter your current password"
                autoFocus
              />
            </div>
            
            <div className="-mx-6 flex justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
              <button
                onClick={cancelRemoveMFAMethod}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveMFAMethod}
                disabled={!passwordConfirmation.trim()}
                className="btn btn-danger"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityTab;
